/** @jsx jsx */

import { Fragment, useState } from 'react';

import { useLocalStorage } from '@zapier/common-hooks';

import {
  BetaMenuTriggerItem,
  BetaMenuTriggerList,
  BetaMenuTriggerPopover,
  BetaMenuTriggerRoot,
  IconButton,
  Text,
  Tooltip,
  TooltipFocusableTrigger,
} from '@zapier/design-system';

import { Border, Colors } from '@zapier/theme';
import { css, jsx } from '@emotion/core';

import ServiceIcon from 'app/developer-v3/components/ServiceIcon';
import StatusLabel from 'app/developer-v3/components/StatusLabel';

import { DISABLED_INTEGRATIONS_KEY } from '../constants';
import { IntegrationType, ItemValue } from '../types';

const Styles = {
  cell: (
    {
      isAction = false,
      isStatus = false,
    }: {
      isAction?: boolean;
      isStatus?: boolean;
    } = {
      isAction: false,
      isStatus: false,
    }
  ) => [
    css`
      line-height: 1.5;
      padding: 15px;
      color: ${Colors.steel};
    `,
    isAction &&
      css`
        width: 16px;
      `,
    isStatus &&
      css`
        white-space: nowrap;
      `,
  ],
  logo: css`
    display: inline-block;
    margin-right: 14px;
    vertical-align: middle;
  `,
  menuItem: css`
    flex: 1;
    padding: 10px;
    text-overflow: ellipsis;
    white-space: nowrap;
  `,
  row: css`
    cursor: pointer;
    vertical-align: middle;
    :not(:last-of-type) {
      border-bottom: 1px solid ${Border.color.default};
    }
    :hover {
      background-color: ${Colors.grey200};
    }
  `,
  status: css`
    align-items: center;
    display: flex;
    gap: 5px;
    white-space: initial;
  `,

  root: css`
    position: relative;
    :not(:last-child) {
      border-bottom: 1px solid ${Border.color.default};
    }
  `,
};

type Props = {
  integration: IntegrationType;
  onViewVersion: (args: {
    id: number;
    isBeta?: boolean;
    isPublic?: boolean;
    title: string;
    version?: string;
  }) => void;
  onViewVersions: (args: { id: number; title: string }) => void;
};

function Integration({ integration, onViewVersion, onViewVersions }: Props) {
  const integrationVersion = integration.version;

  const [disabledIntegrations] = useLocalStorage(DISABLED_INTEGRATIONS_KEY);
  const [isHovered, setIsHovered] = useState(false);

  const onChoose = async (value: ItemValue) => {
    if (disabledIntegrations && disabledIntegrations[integration.id]) {
      return;
    }
    switch (value) {
      case 'view-version':
        onViewVersion({
          id: integration.id,
          isBeta: integration.isBeta,
          isPublic: integration.isPublic,
          title: integration.title,
          version: integrationVersion,
        });
        break;
      case 'view-versions':
        onViewVersions({
          id: integration.id,
          title: integration.title,
        });
        break;
    }
  };

  return (
    <tbody
      css={Styles.root}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {isHovered &&
        disabledIntegrations &&
        disabledIntegrations[integration.id] && (
          <Tooltip
            allowMultiline={true}
            content="Integration version is scheduled for deletion"
          >
            <TooltipFocusableTrigger>
              <Text color="inherit" type="SmallPrint3">
                Integration version is scheduled for deletion
              </Text>
            </TooltipFocusableTrigger>
          </Tooltip>
        )}
      <tr css={Styles.row} onClick={() => onChoose('view-version')}>
        <td css={Styles.cell()}>
          <span css={Styles.logo}>
            <ServiceIcon
              isSymbol={true}
              serviceName={integration.title}
              size={40}
              src={integration.image}
            />
          </span>
          <Text>{integration.title}</Text>
        </td>
        <td css={Styles.cell({ isStatus: true })}>
          <div css={Styles.status}>
            <Fragment>
              <StatusLabel
                label={integration.status.display}
                variant={
                  integration.status.published &&
                  (integration.isPublic || integration.isBeta)
                    ? 'success'
                    : undefined
                }
              />
              {integration.isBeta && (
                <StatusLabel label="Beta" variant="GrayWarm3" />
              )}
            </Fragment>
          </div>
        </td>
        <td css={Styles.cell()}>
          <Text>{integration.version}</Text>
        </td>
        <td aria-label="Actions" css={Styles.cell({ isAction: true })}>
          <div onClick={event => event.stopPropagation()} role="presentation">
            <BetaMenuTriggerRoot>
              <IconButton
                aria-label="menu"
                name="navMoreHoriz"
                variant="ghost"
                size="small"
              />
              <BetaMenuTriggerPopover>
                <BetaMenuTriggerList
                  onAction={key => onChoose(key as ItemValue)}
                >
                  <BetaMenuTriggerItem
                    onAction={() => onChoose('view-version')}
                  >
                    Open
                  </BetaMenuTriggerItem>
                  <BetaMenuTriggerItem
                    onAction={() => onChoose('view-versions')}
                  >
                    View versions
                  </BetaMenuTriggerItem>
                </BetaMenuTriggerList>
              </BetaMenuTriggerPopover>
            </BetaMenuTriggerRoot>
          </div>
        </td>
      </tr>
    </tbody>
  );
}

export default Integration;
